'use client';
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, CheckCircle, XCircle, AlertCircle, Settings, Globe, Key, Clock, Trash2, RefreshCw, User, Building } from 'lucide-react';
import { toast } from 'sonner';
import { getApiConfig } from '@/api/api';
import {Configuration, MeApi} from '@/api/generated';
import { useAuth } from '@/context/AuthContext';
import { useOrg } from '@/context/OrgContext';

interface ApiStatusDialogProps {
    open: boolean;
    onOpenChangeAction: (open: boolean) => void;
}

// Available API endpoints
const API_ENDPOINTS = [
    { value: 'https://api.uat.meersec.com', label: 'UAT - api.uat.meersec.com' },
    { value: 'http://localhost:3500', label: 'Local - localhost:3500' }
];

export function ApiStatusDialog({ open, onOpenChangeAction }: ApiStatusDialogProps) {
    const [connectionStatus, setConnectionStatus] = useState<'checking' | 'connected' | 'disconnected' | 'error'>('checking');
    const [apiEndpoint, setApiEndpoint] = useState<string>('');
    const [selectedEndpoint, setSelectedEndpoint] = useState<string>('');
    const [apiKey, setApiKey] = useState<string>('');
    const [lastChecked, setLastChecked] = useState<Date | null>(null);
    const [errorMessage, setErrorMessage] = useState<string>('');

    // Get auth and org context
    const { user } = useAuth();
    const { orgId } = useOrg();
    const getCurrentEndpoint = () => {
        if (typeof window === 'undefined') return '';
        if (process.env.NEXT_PUBLIC_API_BASE_PATH && process.env.NEXT_PUBLIC_API_BASE_PATH !== '') {
            return process.env.NEXT_PUBLIC_API_BASE_PATH;
        }
        return localStorage.getItem('API_ENDPOINT') || '';
    };


    const checkConnection = async (customEndpoint?: string) => {
        setConnectionStatus('checking');
        setErrorMessage('');

        try {
            const endpoint = customEndpoint || getCurrentEndpoint();
            const apiKeyValue = localStorage.getItem("API_KEY") || '';

            if (!apiKeyValue) {
                setConnectionStatus('error');
                setErrorMessage('No API key found');
                return;
            }

            setApiEndpoint(endpoint);
            setApiKey(apiKeyValue);



            // Create a temporary configuration for testing
            const testConfig: Configuration = {
                basePath: endpoint,
                accessToken: apiKeyValue,
                isJsonMime: function (mime: string): boolean {
                    throw new Error('Function not implemented.');
                }
            };

            // Test the connection by calling the /me endpoint
            const meApi = new MeApi(testConfig);
            await meApi.meControllerMe();

            setConnectionStatus('connected');
            setLastChecked(new Date());
        } catch (error: unknown) {
            setConnectionStatus('disconnected');
            if (error && typeof error === 'object' && 'message' in error && typeof (error as any).message === 'string') {
                setErrorMessage((error as { message: string }).message);
            } else {
                setErrorMessage('Connection failed');
            }
            setLastChecked(new Date());
        }
    };

    const switchEndpoint = async (newEndpoint: string) => {
        if (typeof window === 'undefined') return;
        
        try {
            // Store the new endpoint in localStorage
            localStorage.setItem('API_ENDPOINT', newEndpoint);
            setSelectedEndpoint(newEndpoint);

            // Test connection with new endpoint
            await checkConnection(newEndpoint);

            toast.success(`Switched to ${newEndpoint}`);

            // Suggest page reload for full effect
            setTimeout(() => {
                if (confirm('Endpoint switched! Reload the page to apply changes everywhere?')) {
                    window.location.reload();
                }
            }, 1000);
        } catch (error) {
            toast.error('Failed to switch endpoint');
        }
    };

    const clearCache = () => {
        if (typeof window === 'undefined') return;
        
        try {
            // Define cache keys to clear
            const cacheKeys = [
                'API_ENDPOINT',
                'ORG_ID',
                // Add other cache keys as needed
            ];

            // Clear localStorage items (except API_KEY)
            cacheKeys.forEach(key => {
                localStorage.removeItem(key);
            });

            // Clear sessionStorage
            sessionStorage.clear();

            // Clear any cached API responses if using a cache library
            if ('caches' in window) {
                caches.keys().then(names => {
                    names.forEach(name => {
                        caches.delete(name);
                    });
                });
            }

            toast.success('Cache cleared successfully');

            // Reset to default endpoint
            setSelectedEndpoint(getCurrentEndpoint());
            checkConnection();

        } catch (error) {
            toast.error('Failed to clear cache');
        }
    };

    useEffect(() => {
        if (open) {
            const currentEndpoint = getCurrentEndpoint();
            setSelectedEndpoint(currentEndpoint);
            checkConnection();
        }
    }, [open]);

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        toast.success(`${label} copied to clipboard`);
    };

    const getStatusIcon = () => {
        switch (connectionStatus) {
            case 'connected':
                return <CheckCircle className="h-5 w-5 text-green-500" />;
            case 'disconnected':
                return <XCircle className="h-5 w-5 text-red-500" />;
            case 'error':
                return <AlertCircle className="h-5 w-5 text-yellow-500" />;
            case 'checking':
                return <div className="h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />;
        }
    };

    const getStatusBadge = () => {
        switch (connectionStatus) {
            case 'connected':
                return <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Connected</Badge>;
            case 'disconnected':
                return <Badge variant="destructive">Disconnected</Badge>;
            case 'error':
                return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">Error</Badge>;
            case 'checking':
                return <Badge variant="outline">Checking...</Badge>;
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChangeAction}>
            <DialogContent className="max-w-7xl w-full max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Settings className="h-5 w-5" />
                        Debug Information & API Configuration
                    </DialogTitle>
                </DialogHeader>

                <Tabs defaultValue="api" className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="api">API & Connection</TabsTrigger>
                        <TabsTrigger value="user">User & Organization</TabsTrigger>
                    </TabsList>

                    <TabsContent value="api" className="space-y-6 mt-6">
                        {/* Endpoint Switcher */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <RefreshCw className="h-4 w-4" />
                                    Switch API Endpoint
                                </CardTitle>
                                <CardDescription>
                                    Change the API endpoint and test connectivity
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex gap-2">
                                    <Select value={selectedEndpoint} onValueChange={switchEndpoint}>
                                        <SelectTrigger className="flex-1">
                                            <SelectValue placeholder="Select an endpoint" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {API_ENDPOINTS.map((endpoint) => (
                                                <SelectItem key={endpoint.value} value={endpoint.value}>
                                                    {endpoint.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Button
                                        variant="outline"
                                        onClick={clearCache}
                                        className="flex items-center gap-2"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                        Clear Cache
                                    </Button>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Switching endpoints will test the connection and may require a page reload for full effect.
                                </p>
                            </CardContent>
                        </Card>

                        {/* Connection Status Overview */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    {getStatusIcon()}
                                    Connection Status
                                </CardTitle>
                                <CardDescription>
                                    Current status of the API connection
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium">Status:</span>
                                    {getStatusBadge()}
                                </div>

                                {lastChecked && (
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium">Last Checked:</span>
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <Clock className="h-4 w-4" />
                                            {lastChecked.toLocaleString()}
                                        </div>
                                    </div>
                                )}

                                {errorMessage && (
                                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                                        <p className="text-sm text-red-800 dark:text-red-200">{errorMessage}</p>
                                    </div>
                                )}

                                <Button onClick={() => checkConnection()} disabled={connectionStatus === 'checking'} className="w-full">
                                    {connectionStatus === 'checking' ? 'Checking...' : 'Test Connection'}
                                </Button>
                            </CardContent>
                        </Card>

                        <Separator />

                        {/* API Configuration Details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* API Endpoint */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Globe className="h-4 w-4" />
                                        Current API Endpoint
                                    </CardTitle>
                                    <CardDescription>
                                        Active base URL for API requests
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div className="flex items-center gap-2">
                                        <code className="flex-1 px-3 py-2 bg-muted rounded-md text-sm font-mono break-all">
                                            {apiEndpoint || 'Not configured'}
                                        </code>
                                        {apiEndpoint && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => copyToClipboard(apiEndpoint, 'API Endpoint')}
                                            >
                                                <Copy className="h-4 w-4" />
                                            </Button>
                                        )}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        Environment: {process.env.NODE_ENV || 'development'}
                                    </p>
                                </CardContent>
                            </Card>

                            {/* API Key */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Key className="h-4 w-4" />
                                        API Key
                                    </CardTitle>
                                    <CardDescription>
                                        Authentication token for API access
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div className="flex items-center gap-2">
                                        <code className="flex-1 px-3 py-2 bg-muted rounded-md text-sm font-mono">
                                            {apiKey ? `${apiKey.substring(0, 8)}...${apiKey.substring(apiKey.length - 4)}` : 'Not found'}
                                        </code>
                                        {apiKey && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => copyToClipboard(apiKey, 'API Key')}
                                            >
                                                <Copy className="h-4 w-4" />
                                            </Button>
                                        )}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {apiKey ? 'Key is present and masked for security' : 'No API key found in localStorage'}
                                    </p>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Environment Variables */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Environment Configuration</CardTitle>
                                <CardDescription>
                                    Current environment variables and runtime settings
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium">NEXT_PUBLIC_API_BASE_PATH</label>
                                            <code className="block mt-1 px-3 py-2 bg-muted rounded-md text-sm font-mono break-all">
                                                {process.env.NEXT_PUBLIC_API_BASE_PATH || 'Not set'}
                                            </code>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">Runtime Endpoint Override</label>
                                            <code className="block mt-1 px-3 py-2 bg-muted rounded-md text-sm font-mono break-all">
                                                {typeof window !== 'undefined' ? (localStorage.getItem('API_ENDPOINT') || 'Not set') : 'Not set'}
                                            </code>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">NODE_ENV</label>
                                            <code className="block mt-1 px-3 py-2 bg-muted rounded-md text-sm font-mono">
                                                {process.env.NODE_ENV || 'development'}
                                            </code>
                                        </div>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-3">
                                        Environment variables are set at build time. Runtime endpoint overrides are stored in localStorage.
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="user" className="space-y-6 mt-6">
                        {/* Current User Information */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <User className="h-4 w-4" />
                                    Current User
                                </CardTitle>
                                <CardDescription>
                                    Information about the currently logged-in user
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium">User ID</label>
                                        <div className="flex items-center gap-2 mt-1">
                                            <code className="flex-1 px-3 py-2 bg-muted rounded-md text-sm font-mono">
                                                {user?.id || 'Not available'}
                                            </code>
                                            {user?.id && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => copyToClipboard(user.id, 'User ID')}
                                                >
                                                    <Copy className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Email</label>
                                        <div className="flex items-center gap-2 mt-1">
                                            <code className="flex-1 px-3 py-2 bg-muted rounded-md text-sm font-mono">
                                                {user?.email || 'Not available'}
                                            </code>
                                            {user?.email && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => copyToClipboard(user.email, 'Email')}
                                                >
                                                    <Copy className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Name</label>
                                        <code className="block mt-1 px-3 py-2 bg-muted rounded-md text-sm font-mono">
                                            {user?.firstName + ' ' + user?.lastName || 'Not available'}
                                        </code>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Status</label>
                                        <div className="mt-1">
                                            <Badge variant={user ? "secondary" : "destructive"} className={user ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" : ""}>
                                                {user ? 'Authenticated' : 'Not authenticated'}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Current Organization Information */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Building className="h-4 w-4" />
                                    Current Organization
                                </CardTitle>
                                <CardDescription>
                                    Information about the currently selected organization
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium">Organization ID</label>
                                        <div className="flex items-center gap-2 mt-1">
                                            <code className="flex-1 px-3 py-2 bg-muted rounded-md text-sm font-mono">
                                                {orgId || 'Not available'}
                                            </code>
                                            {orgId && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => copyToClipboard(orgId, 'Organization ID')}
                                                >
                                                    <Copy className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Organization Name</label>
                                        <code className="block mt-1 px-3 py-2 bg-muted rounded-md text-sm font-mono">
                                            {orgId ? `Organization ${orgId}` : 'Not available'}
                                        </code>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Status</label>
                                        <div className="mt-1">
                                            <Badge variant={orgId ? "secondary" : "destructive"} className={orgId ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" : ""}>
                                                {orgId ? 'Selected' : 'No organization selected'}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Context Information */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Context Information</CardTitle>
                                <CardDescription>
                                    Additional context and session information
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium">Session Storage Keys</label>
                                            <code className="block mt-1 px-3 py-2 bg-muted rounded-md text-sm font-mono">
                                                {typeof window !== 'undefined' ? Object.keys(sessionStorage).length : 0} keys
                                            </code>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">Local Storage Keys</label>
                                            <code className="block mt-1 px-3 py-2 bg-muted rounded-md text-sm font-mono">
                                                {typeof window !== 'undefined' ? Object.keys(localStorage).length : 0} keys
                                            </code>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">Current URL</label>
                                            <code className="block mt-1 px-3 py-2 bg-muted rounded-md text-sm font-mono break-all">
                                                {typeof window !== 'undefined' ? window.location.href : 'Not available'}
                                            </code>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">User Agent</label>
                                            <code className="block mt-1 px-3 py-2 bg-muted rounded-md text-sm font-mono break-all">
                                                {typeof window !== 'undefined' ? navigator.userAgent.substring(0, 50) + '...' : 'Not available'}
                                            </code>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Actions */}
                    <div className="flex justify-end gap-2 mt-6">
                        <Button variant="outline" onClick={() => onOpenChangeAction(false)}>
                            Close
                        </Button>
                        <Button onClick={() => checkConnection()} disabled={connectionStatus === 'checking'}>
                            Refresh Status
                        </Button>
                    </div>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
}
